import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

/**
 * Reads filter values a link carried in the URL (a dashboard row's
 * `/payments?verification_status=pending`, say) when the page mounts, and
 * then takes them out of the URL. The page keeps them as its own filter
 * state from there, like any filter the person picks, so a later sidebar
 * click or Back never leaves the URL and the filters saying different things.
 *
 * A link followed while the page is already open (a push tapped on
 * ที่จอดรถวันนี้, EP-161) puts values in the URL again: they are read the
 * same way and returned as a new object, so a page that wants to follow
 * them can react to the change. A page that only reads the values into its
 * initial state keeps working as before.
 */
export function useDeepLinkParams<K extends string>(keys: readonly K[]): Partial<Record<K, string>> {
  const [searchParams, setSearchParams] = useSearchParams()
  const read = () => {
    const out: Partial<Record<K, string>> = {}
    for (const key of keys) {
      const value = searchParams.get(key)
      if (value !== null && value !== '') out[key] = value
    }
    return out
  }
  const [values, setValues] = useState(read)
  const search = searchParams.toString()
  const present = keys.some((key) => searchParams.has(key))

  // Adjusted while rendering, not in an effect: the new values are there on
  // the render that sees the new URL. Comparing with the last URL seen (not
  // the last one read) lets the same link, followed twice, count twice.
  const [lastSearch, setLastSearch] = useState(search)
  if (search !== lastSearch) {
    setLastSearch(search)
    if (present) setValues(read())
  }

  useEffect(() => {
    if (!present) return
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      for (const key of keys) next.delete(key)
      return next
    }, { replace: true })
    // On each URL that carries a value: the values are already read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])
  return values
}
